import { ROOM_STATE } from 'asteroids-shared/protocol'
import ClientGame from '../ClientGame.js'
import { formatDeathmatchCountdown, formatTeamScore, renderHUD, renderPlayers } from './HUD.js'
import { renderPostMatchDialog } from './GameOver.js'

const POST_MATCH_REVEAL_DELAY_MS = 1400

export default class Game {
  constructor(root, {
    socket, localPlayerId, room, audio,
    spawn = { playerOrder: [] }, gameOver
  }) {
    this.root = root
    this.socket = socket
    this.localPlayerId = localPlayerId
    this.room = room
    this.spawn = spawn
    this.audio = audio
    this.gameOver = gameOver
    this.engine = null
    this.roomId = room.roomId ?? null
    this.postMatchTimer = null
    this.postMatchVisible = false
    this.postMatchRoot = null
  }

  mount() {
    this.root.innerHTML = `
      ${renderHUD(this.room.roster, this.localPlayerId, this.room.mode ?? 'coop')}
      <canvas></canvas>
      <div data-role="post-match-root"></div>
    `

    this.hud = {
      score: this.root.querySelector('[data-role="hud-score"]'),
      lives: this.root.querySelector('[data-role="hud-lives"]'),
      shields: this.root.querySelector('[data-role="hud-shields"]'),
      shieldsFill: this.root.querySelector('.hud-shields-fill'),
      shieldsCount: this.root.querySelector('.hud-shields-count'),
      timer: this.root.querySelector('[data-role="hud-timer"]')
    }
    this.postMatchRoot = this.root.querySelector('[data-role="post-match-root"]')

    this.engine = new ClientGame({
      room: this.room,
      spawn: this.spawn,
      localPlayerId: this.localPlayerId,
      canvas: this.root.querySelector('canvas'),
      socket: this.socket,
      audio: this.audio,
      hud: {
        score: this.gameOver && this.gameOver.mode === 'coop'
          ? (this.gameOver.finalScore ?? 0)
          : 0,
        lives: this.gameOver && this.gameOver.mode === 'coop'
          ? 0
          : 3,
        teamScores: this.gameOver?.teamScores ?? null,
        onChange: next => this.updateHud(next),
        feed: {
          onChange: () => this.updateFeed()
        }
      }
    })

    this.engine.start()
    this.updateGameOver(this.gameOver)
    this.updateFeed()
  }

  updateRoster(lobby) {
    const { players, matchEndsAtServerTime: _omitFromWire, ...wire } = lobby
    Object.assign(this.room, wire)
    this.room.roster = players ?? []
    if (Number.isFinite(lobby.matchEndsAtServerTime)) {
      if (!this.room.round) {
        this.room.round = { start: { serverTime: null, roundDuration: null }, end: null }
      }
      this.room.round.end = lobby.matchEndsAtServerTime
    } else if (lobby.state !== ROOM_STATE.PLAYING) {
      if (this.room.round?.end != null) this.room.round.end = null
    }
    this.engine?.updateRoster(lobby)
    this.root.querySelector('.hud-players').innerHTML = renderPlayers(this.room.roster, this.localPlayerId)
  }

  updateGameOver(gameOver) {
    this.gameOver = gameOver ?? null
    if (!this.engine) return

    if (!this.gameOver) {
      clearTimeout(this.postMatchTimer)
      this.postMatchTimer = null
      this.postMatchVisible = false
      this.postMatchRoot.innerHTML = ''
      this.engine.setInputEnabled(true)
      return
    }

    if (this.gameOver.mode === 'coop') {
      this.engine.hud.score = this.gameOver.finalScore ?? this.engine.hud.score
      this.engine.hud.lives = 0
      this.engine.pushHud()
    }

    this.engine.setInputEnabled(false)

    if (this.postMatchVisible) {
      this.renderPostMatchDialog()
      return
    }

    if (this.postMatchTimer) return

    if (POST_MATCH_REVEAL_DELAY_MS <= 0) {
      this.postMatchVisible = true
      this.renderPostMatchDialog()
      return
    }

    this.postMatchTimer = setTimeout(() => {
      this.postMatchTimer = null
      this.postMatchVisible = true
      this.renderPostMatchDialog()
    }, POST_MATCH_REVEAL_DELAY_MS)
  }

  renderPostMatchDialog() {
    if (!this.postMatchRoot || !this.gameOver) return
    this.postMatchRoot.innerHTML = renderPostMatchDialog(this.gameOver, this.room, this.localPlayerId)

    this.postMatchRoot.querySelector('[data-role="return-to-lobby"]')?.addEventListener('click', () => {
      this.room.returnLobby?.()
    })

    const postMatchDialog = this.postMatchRoot.querySelector('dialog.post-match-modal')
    if (!postMatchDialog) return
    postMatchDialog.addEventListener('cancel', e => {
      e.preventDefault()
    })
    try {
      postMatchDialog.showModal()
    } catch {
      postMatchDialog.setAttribute('open', '')
    }
  }

  updateHud(hud) {
    const mode = hud.mode ?? this.room.mode ?? 'coop'
    this.root.querySelector('.hud')?.setAttribute('data-hud-mode', mode)
    this.hud.score.textContent = `Score: ${hud.score}`
    const shieldsActive = (hud.shieldMax ?? 0) > 0
    if (this.hud.lives) {
      if (shieldsActive && mode === 'coop') {
        this.hud.lives.hidden = true
      } else {
        this.hud.lives.hidden = false
        this.hud.lives.textContent = mode === 'ctf'
          ? formatTeamScore(hud.teamScores)
          : `Lives: ${hud.lives}`
      }
    }
    if (this.hud.shields) {
      if (shieldsActive) {
        this.hud.shields.hidden = false
        const max = Math.max(1, hud.shieldMax)
        const cur = Math.max(0, Math.min(max, hud.shieldHealth ?? 0))
        const p = cur / max
        if (this.hud.shieldsFill) this.hud.shieldsFill.style.setProperty('--p', String(p))
        if (this.hud.shieldsCount) this.hud.shieldsCount.textContent = `${cur}/${max}`
        const color = hud.shieldColor || '#fff'
        this.hud.shields.style.color = color
      } else {
        this.hud.shields.hidden = true
      }
    }
    if (hud.roundRemainingMs == null) {
      this.hud.timer.textContent = ''
    } else {
      this.hud.timer.textContent = formatDeathmatchCountdown(hud.roundRemainingMs)
    }
  }

  destroy() {
    clearTimeout(this.postMatchTimer)
    this.postMatchTimer = null
    this.postMatchRoot?.querySelector('dialog.post-match-modal')?.close()
    this.engine?.stop()
    this.engine = null
  }

  updateFeed() {
    const el = this.root.querySelector('.hud-feed')
    if (!el || !this.engine) return
    el.innerHTML = this.engine.hud.feed.list.map(item => `<li>${item.text}</li>`).join('')
  }
}
